import { AnimationCurve, AnimationCurveJson } from '@easytwin/runtime';
import { useEffect, useRef } from 'react';
import { Canvas2DRendererPlugin, CanvasEditor, Curve, ResizePlugin } from '@easytwin/canvas-editor';
import { Trigger } from '@easyv/react-components';
import * as React from 'react';
import { getBoundsOfCurveByKeys, isNil } from '@/utils';
import { CurveEditor } from './CurveEditor';
import { DEFAULT_CURVE_VALUE } from './defaultCurveValue';

interface CurveEditorTriggerProps {
  value?: AnimationCurveJson;
  onChange?: (value: AnimationCurveJson) => void;
}

export function CurveEditorTrigger(props: CurveEditorTriggerProps) {
  const { value, onChange } = props;
  const container = useRef<HTMLDivElement | null>(null);
  const curve = useRef<Curve>();
  const curvesEditor = useRef<CanvasEditor>();

  useEffect(() => {
    if (isNil(container.current)) return;

    const editor = new CanvasEditor(
      {
        container: container.current,
        width: container.current.clientWidth,
        height: container.current.clientHeight,
        devicePixelRatio: 2,
      },
      [new Canvas2DRendererPlugin(), new ResizePlugin()],
    );
    curvesEditor.current = editor;
    const c = editor.createElement(Curve, {
      style: {
        disable: true,
        strokeStyle: '#ffffffff',
        lineWidth: 1,
      },
    });
    curve.current = c;
    editor.root.addChild(c);

    return () => {
      editor.dispose();
    };
  }, []);

  useEffect(() => {
    if (!curve.current || !curvesEditor.current || !container.current) return;
    const c = AnimationCurve.fromJSON(value || DEFAULT_CURVE_VALUE);

    const width = container.current.clientWidth;
    const height = container.current.clientHeight;
    const camera = curvesEditor.current.camera;
    const { minx, maxx, miny, maxy } = getBoundsOfCurveByKeys(c.keys);

    // remap curve value to fit content
    c.keys.forEach((key) => {
      //  minx ~ maxx to 0 ~ width
      const scaleX = width / (maxx - minx);
      const scaleY = height / (maxy - miny);
      key.time = (key.time - minx) * scaleX;
      key.value = (key.value - miny) * scaleY;

      const angle = Math.atan(key.inTangent);
      const keyWeightY = key.inWeight * Math.sin(angle) * scaleY;
      const keyWeightX = key.inWeight * Math.cos(angle) * scaleX;
      key.inWeight = Math.sqrt(keyWeightX * keyWeightX + keyWeightY * keyWeightY);
      key.inTangent *= scaleY / scaleX;

      const angle2 = Math.atan(key.outTangent);
      const keyWeightY2 = key.outWeight * Math.sin(angle2) * scaleY;
      const keyWeightX2 = key.outWeight * Math.cos(angle2) * scaleX;
      key.outWeight = Math.sqrt(keyWeightX2 * keyWeightX2 + keyWeightY2 * keyWeightY2);
      key.outTangent *= scaleY / scaleX;
    });

    // FIXME: canvas-editor 还是依赖easytwin/engine的curve
    curve.current.setCurve(c as any);
    camera.setFlipY(true);
  }, [value]);

  return (
    <div>
      <Trigger
        containerScrollToClose
        trigger='click'
        position='bl'
        popupAlign={{ bottom: 4 }}
        popup={() => <CurveEditor value={value} onChange={onChange} />}
      >
        <div className='flex-1 border rounded hover:border-primary border-FFFFFF1F bg-FFFFFF0A h-7'>
          <div className='w-full h-full' ref={container}></div>
        </div>
      </Trigger>
    </div>
  );
}
